(() => {
  if (window.EMISHomeInteractions) {
    window.EMISHomeInteractions.init(document);
    return;
  }

  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const states = new Map();
  let numberFormat;
  try {
    numberFormat = new Intl.NumberFormat(document.documentElement.lang || 'en', { maximumFractionDigits: 0 });
  } catch (error) {
    numberFormat = new Intl.NumberFormat('en', { maximumFractionDigits: 0 });
  }

  const findSections = (root, selector) => {
    const sections = [...root.querySelectorAll(selector)];
    if (root.matches && root.matches(selector)) sections.unshift(root);
    return sections;
  };

  const initMarketplaces = (section) => {
    if (states.has(section)) return;
    const primaryGroup = section.querySelector('.ems-marketplaces__group');
    if (!primaryGroup || !primaryGroup.querySelector('a')) return;
    const state = { kind: 'marketplaces' };
    state.update = () => {
      section.dataset.emsReady = motionPreference.matches ? 'false' : 'true';
    };
    state.cleanup = () => {};
    states.set(section, state);
    state.update();
  };

  const initStats = (section) => {
    if (states.has(section)) return;
    const counters = [...section.querySelectorAll('[data-ems-counter]')].map((element) => {
      const rawTarget = Number(element.dataset.target);
      const target = Number.isFinite(rawTarget) ? Math.min(1000000000, Math.max(0, Math.round(rawTarget))) : 0;
      return { element, target, frame: null };
    });
    if (!counters.length) return;
    const state = { kind: 'stats', started: false, observer: null, active: true, metricsTimer: null, request: null };
    const finish = () => {
      counters.forEach((counter) => {
        if (counter.frame !== null) cancelAnimationFrame(counter.frame);
        counter.frame = null;
        counter.element.textContent = numberFormat.format(counter.target);
      });
    };
    const start = () => {
      if (state.started) return;
      state.started = true;
      if (state.observer) state.observer.disconnect();
      if (motionPreference.matches) {
        finish();
        return;
      }
      const duration = 1500;
      counters.forEach((counter) => {
        counter.element.textContent = numberFormat.format(0);
        let startTime;
        const tick = (time) => {
          if (motionPreference.matches || !counter.element.isConnected) {
            finish();
            return;
          }
          if (startTime === undefined) startTime = time;
          const progress = Math.min(1, (time - startTime) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          counter.element.textContent = numberFormat.format(Math.round(counter.target * eased));
          if (progress < 1) counter.frame = requestAnimationFrame(tick);
          else counter.frame = null;
        };
        counter.frame = requestAnimationFrame(tick);
      });
    };
    state.update = () => {
      if (motionPreference.matches) {
        state.started = true;
        if (state.observer) state.observer.disconnect();
        finish();
      }
    };
    state.cleanup = () => {
      state.active = false;
      if (state.observer) state.observer.disconnect();
      if (state.metricsTimer !== null) clearInterval(state.metricsTimer);
      if (state.request) state.request.abort();
      document.removeEventListener('visibilitychange', state.refreshMetrics);
      finish();
    };
    state.refreshMetrics = async () => {
      if (!state.active || document.visibilityState === 'hidden' || state.request || !state.metricsURL) return;
      const controller = new AbortController();
      state.request = controller;
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(state.metricsURL, { credentials: 'omit', cache: 'no-store', redirect: 'error', signal: controller.signal });
        if (!response.ok) return;
        const metrics = await response.json();
        if (!state.active || metrics.source !== 'bigseller' || metrics.scope !== 'emis') return;
        const asOf = Date.parse(metrics.as_of);
        if (!Number.isFinite(asOf) || asOf > Date.now() + 300000 || asOf < Date.now() - 172800000) return;
        const baseline = Number(section.dataset.emsHistoricalOrders) || 0;
        let changed = false;
        counters.forEach((counter) => {
          const metric = counter.element.dataset.emsMetric;
          const recorded = metric === 'orders' ? metrics.orders_fulfilled : metric === 'reviews' ? metrics.reviews_five_star : undefined;
          if (!Number.isSafeInteger(recorded) || recorded < 0) return;
          const target = recorded + (metric === 'orders' ? baseline : 0);
          if (target <= counter.target || target > 1000000000) return;
          counter.target = target;
          counter.element.dataset.target = String(target);
          counter.element.classList.toggle('ems-stat__value--large', target >= 1000000);
          const accessible = section.querySelector(`[data-ems-metric-value="${metric}"]`);
          if (accessible) accessible.textContent = numberFormat.format(target) + (accessible.dataset.emsSuffix || '');
          changed = true;
        });
        if (changed) {
          finish();
          section.dataset.emsMetricsAsOf = metrics.as_of;
        }
      } catch (error) {
        // Keep the last confirmed values if the aggregate feed is unavailable.
      } finally {
        clearTimeout(timeout);
        if (state.request === controller) state.request = null;
      }
    };
    states.set(section, state);
    finish();
    if (section.dataset.emsMetricsUrl) {
      try {
        const metricsURL = new URL(section.dataset.emsMetricsUrl, window.location.origin);
        if (metricsURL.protocol === 'https:' && !metricsURL.username && !metricsURL.password) {
          state.metricsURL = metricsURL.href;
          state.refreshMetrics();
          state.metricsTimer = setInterval(state.refreshMetrics, 60000);
          document.addEventListener('visibilitychange', state.refreshMetrics);
        }
      } catch (error) {
        // Invalid feed settings leave the confirmed snapshot visible.
      }
    }
    if (motionPreference.matches || !('IntersectionObserver' in window)) {
      state.started = true;
      return;
    }
    state.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) start();
    }, { threshold: 0.3 });
    state.observer.observe(section);
  };

  const init = (root) => {
    findSections(root, '[data-ems-marketplaces]').forEach(initMarketplaces);
    findSections(root, '[data-ems-stats]').forEach(initStats);
  };
  const cleanup = (root) => {
    for (const [section, state] of states) {
      if (root === section || root.contains(section)) {
        state.cleanup();
        states.delete(section);
      }
    }
  };

  window.EMISHomeInteractions = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanup(event.target));
  const updateMotion = () => states.forEach((state) => state.update());
  if (motionPreference.addEventListener) motionPreference.addEventListener('change', updateMotion);
  else motionPreference.addListener(updateMotion);
})();
